import { useLayoutEffect, useState } from 'react'
import type { RefObject } from 'react'

export interface PopupPosition {
  top: number
  left: number
  width: number
}

export interface UsePortalPopupOptions {
  /**
   * Offset from the anchor element in pixels
   * @default 8
   */
  offset?: number
  /**
   * Position of the popup relative to the anchor
   * @default 'top'
   */
  placement?: 'top' | 'bottom' | 'left' | 'right'
}

/**
 * Hook for calculating popup position relative to an anchor element.
 * Returns viewport-relative coordinates for use with position: fixed.
 * 
 * @param isOpen - Whether the popup is open
 * @param anchorRef - Reference to the anchor element
 * @param options - Configuration options
 * @returns Popup position object with top, left (viewport coords), and width
 */
export const usePortalPopup = (
  isOpen: boolean,
  anchorRef: RefObject<HTMLElement | null>,
  options: UsePortalPopupOptions = {}
): PopupPosition | null => {
  const { offset = 8, placement = 'top' } = options
  const [position, setPosition] = useState<PopupPosition | null>(null)

  useLayoutEffect(() => {
    if (!isOpen || !anchorRef.current) {
      setPosition(null)
      return
    }

    const updatePosition = () => {
      if (!anchorRef.current) return

      // getBoundingClientRect returns viewport-relative coordinates
      const rect = anchorRef.current.getBoundingClientRect()
      let top: number
      let left: number

      switch (placement) {
        case 'top':
          top = rect.top - offset
          left = rect.left
          break
        case 'bottom':
          top = rect.bottom + offset
          left = rect.left
          break
        case 'left':
          top = rect.top
          left = rect.left - offset
          break
        case 'right':
          top = rect.top
          left = rect.right + offset
          break
      }

      setPosition({
        top,
        left,
        width: rect.width,
      })
    }

    updatePosition()

    // Update position on scroll and resize (anchor moves in viewport on scroll)
    window.addEventListener('scroll', updatePosition, true)
    window.addEventListener('resize', updatePosition)

    return () => {
      window.removeEventListener('scroll', updatePosition, true)
      window.removeEventListener('resize', updatePosition)
    }
  }, [isOpen, anchorRef, offset, placement])

  return position
}
